import { createTheme } from '@mui/material/styles';

// ─────────────────────────────────────────────────────────────────────────
// Nexal Admin design system (generated via ui-ux-pro-max skill, 2026-05-26).
// Pattern   : Real-Time / Operations (admin / ops dashboard)
// Style     : Data-Dense Dashboard — minimal padding, content-first, grid
// Typography: Fira Sans (body) + Fira Code (headings/data) — technical, precise
// Colors    : Brand pink primary + cream accent on a warm off-white canvas
//             #EA0A5A — primary pink (AppBar, brand banner, primary buttons, focus rings)
//             #FFFFBF — pale cream (secondary accent / highlights)
//             #F8F8E8 — page background (cream-tinted)
// Effects   : Row hover highlight, soft elevation, 150-300ms transitions,
//             visible focus rings, reduced-motion respected globally.
// ─────────────────────────────────────────────────────────────────────────

const BRAND = {
    pink: '#EA0A5A',
    pinkDark: '#B70747',
    pinkLight: '#FF5B89',
    cream: '#FFFFBF',
    creamDark: '#F0E59C',
    pageBg: '#F8F8E8',
};

const NEUTRAL = {
    surface: '#FFFFFF',
    border: '#E5E1D0',          // warm divider that reads on the cream canvas
    text: 'rgba(20, 14, 18, 0.92)',
    textSecondary: 'rgba(20, 14, 18, 0.65)',
    textDisabled: 'rgba(20, 14, 18, 0.38)',
};

const SANS = '"Fira Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const MONO = '"Fira Code", ui-monospace, SFMono-Regular, Menlo, monospace';

const theme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: BRAND.pink,
            dark: BRAND.pinkDark,
            light: BRAND.pinkLight,
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: BRAND.cream,
            dark: BRAND.creamDark,
            contrastText: 'rgba(0,0,0,0.87)',
        },
        background: {
            default: BRAND.pageBg,
            paper: NEUTRAL.surface,
        },
        text: {
            primary: NEUTRAL.text,
            secondary: NEUTRAL.textSecondary,
            disabled: NEUTRAL.textDisabled,
        },
        divider: NEUTRAL.border,
    },
    shape: {
        borderRadius: 10,
    },
    typography: {
        fontFamily: SANS,
        // Fira Code on headings gives data dashboards that "precise / technical"
        // feel while body stays readable in Fira Sans.
        h1: { fontFamily: MONO, fontWeight: 700 },
        h2: { fontFamily: MONO, fontWeight: 700 },
        h3: { fontFamily: MONO, fontWeight: 700 },
        h4: {
            fontFamily: MONO,
            fontWeight: 700,
            fontSize: '1.5rem',
            letterSpacing: '-0.01em',
            '@media (min-width:600px)': { fontSize: '2rem' },
        },
        h5: {
            fontFamily: MONO,
            fontWeight: 600,
            fontSize: '1.15rem',
            '@media (min-width:600px)': { fontSize: '1.4rem' },
        },
        h6: { fontFamily: MONO, fontWeight: 600, fontSize: '1rem' },
        button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.01em' },
        // Monospace tabular figures keep numeric columns column-aligned without
        // layout shift — a data-dense dashboard table requirement.
        body2: { fontVariantNumeric: 'tabular-nums' },
        caption: { fontVariantNumeric: 'tabular-nums' },
    },
    components: {
        // Honour OS-level "reduce motion" globally — every transition becomes
        // a near-instant snap, animations stop. WCAG / Apple HIG requirement.
        MuiCssBaseline: {
            styleOverrides: {
                '@media (prefers-reduced-motion: reduce)': {
                    '*, *::before, *::after': {
                        animationDuration: '0.001ms !important',
                        animationIterationCount: '1 !important',
                        transitionDuration: '0.001ms !important',
                        scrollBehavior: 'auto !important',
                    },
                },
                body: {
                    backgroundColor: BRAND.pageBg,
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    // Flat brand pink — drop MUI's default gradient overlay.
                    backgroundImage: 'none',
                    boxShadow: '0 1px 2px rgba(20, 14, 18, 0.06), 0 1px 3px rgba(234, 10, 90, 0.12)',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                },
                elevation2: {
                    boxShadow: '0 1px 2px rgba(20, 14, 18, 0.04), 0 2px 6px rgba(20, 14, 18, 0.06)',
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 600,
                    transition: 'background-color 150ms ease-out, box-shadow 150ms ease-out, transform 80ms ease-out',
                    // Visible focus ring for keyboard nav (WCAG 2.4.7).
                    '&:focus-visible': {
                        outline: `2px solid ${BRAND.pink}`,
                        outlineOffset: 2,
                    },
                    '&:active': {
                        transform: 'translateY(1px)',
                    },
                },
                containedPrimary: {
                    '&:hover': {
                        backgroundColor: BRAND.pinkDark,
                    },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    transition: 'background-color 150ms ease-out',
                    '&:focus-visible': {
                        outline: `2px solid ${BRAND.pink}`,
                        outlineOffset: 2,
                    },
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 600,
                    minHeight: 44, // 44px tap target (Apple HIG)
                    '&:focus-visible': {
                        outline: `2px solid ${BRAND.pink}`,
                        outlineOffset: -2,
                    },
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderColor: NEUTRAL.border,
                },
                head: {
                    fontWeight: 700,
                    fontFamily: MONO,
                    fontSize: '0.8125rem',
                    letterSpacing: '0.02em',
                    textTransform: 'uppercase',
                    color: NEUTRAL.textSecondary,
                    backgroundColor: BRAND.pageBg,
                },
                body: {
                    fontVariantNumeric: 'tabular-nums',
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    transition: 'background-color 150ms ease-out',
                    // Data-dense pattern: highlight the row under the cursor so
                    // the eye doesn't lose its place when scanning columns.
                    '&:hover': {
                        backgroundColor: 'rgba(234, 10, 90, 0.04)',
                    },
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    transition: 'background-color 150ms ease-out, color 150ms ease-out',
                    '&:focus-visible': {
                        outline: `2px solid ${BRAND.pink}`,
                        outlineOffset: -2,
                    },
                    '&.Mui-selected': {
                        backgroundColor: 'rgba(234, 10, 90, 0.10)',
                        color: BRAND.pinkDark,
                        '& .MuiListItemIcon-root': { color: BRAND.pinkDark },
                        '&:hover': { backgroundColor: 'rgba(234, 10, 90, 0.14)' },
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    fontWeight: 500,
                    letterSpacing: '0.01em',
                },
            },
        },
        MuiLink: {
            defaultProps: {
                underline: 'hover',
            },
            styleOverrides: {
                root: {
                    color: BRAND.pinkDark,
                    fontWeight: 500,
                    '&:focus-visible': {
                        outline: `2px solid ${BRAND.pink}`,
                        outlineOffset: 2,
                        borderRadius: 2,
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    backgroundColor: NEUTRAL.surface,
                },
            },
        },
    },
});

export default theme;
